/**
 * FlexWorkspace - Tabbed FLEX DSL Scripting Environment
 *
 * Multi-tab IDE for Flex scripting with each tab containing:
 * - Code editor panel
 * - Input context panel
 * - Output console panel
 *
 * Features:
 * - Multiple script tabs
 * - Monaco-based code editor with syntax highlighting
 * - Script management (create, edit, save, delete)
 * - Script execution with trace support
 * - Per-tab input context and output state
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Collapse,
  Checkbox,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Code as CodeIcon,
  PlayArrow as RunIcon,
  Save as SaveIcon,
  Description as ScriptIcon,
  MoreVert as MoreIcon,
  BugReport as TraceIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  ArrowDropDown as DropdownIcon,
  TrackChanges as TrackIcon,
  Schedule as BackgroundIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  Search as SearchIcon,
  AccountTree as TreeIcon,
  Download as ExportIcon,
  CloudSync as SyncIcon,
  Upload as ImportIcon,
  ClearAll as CloseAllIcon,
  Refresh as RefreshIcon,
  CloudDownload as SyncFromIcon,
  DeleteForever as DeleteIcon,
  SmartToy as AgentIcon,
  Dashboard as WorkspaceIcon,
  Edit as EditIcon,
  ContentCopy as DuplicateIcon,
  UnfoldLess as CollapseIcon,
  CreateNewFolder as AddFolderIcon,
  NoteAdd as AddScriptIcon,
  Bolt as InvokableIcon,
  CallSplit as OverrideIcon,
  Layers as OverriddenIcon,
} from '@mui/icons-material';
import { useTheme as useAppTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import ReadOnlyJsonField from '../../FlexStudio/ReadOnlyJsonField';
import FlexEditor from '../../FlexStudio/FlexEditor';
import FlexContextPanel from '../../FlexStudio/FlexContextPanel';
import FlexOutputConsole from '../../FlexStudio/FlexOutputConsole';
import FlexService from '../../../services/FlexService';
import PermissionService from '../../../services/PermissionService';
import SyncFromFilesPanel from './SyncFromFilesPanel';

// Generate unique tab ID
let flexTabIdCounter = 0;
const generateTabId = () => `flex-tab-${++flexTabIdCounter}`;

/**
 * Create a new tab with default state
 */
const createNewTab = (script = null) => ({
  id: generateTabId(),
  script: script, // null for new unsaved script
  scriptId: script?.id || null,
  name: script?.name || 'Untitled',
  content: script?.scriptBody || '',
  originalContent: script?.scriptBody || '',
  isModified: false,
  executionContext: {},
  output: null,
  isRunning: false,
  traceEnabled: false,
  diagnostics: [], // Syntax validation diagnostics
});

/**
 * FlexWorkspace Component
 */
const FlexWorkspace = ({ colors: themeColors, initialScriptId }) => {
  const { themeId } = useAppTheme();
  const { user, hasRole, selectedConnectedSystem } = useAuth();

  // ThemeContext exposes nested color groups (text.primary, primary.main, warning.main, ...)
  // but this component was written against a flat shape (colors.textPrimary, colors.accent,
  // colors.warning, ...). Those flat keys don't exist on the raw theme object, so every sx
  // that referenced them silently no-opped (invalid CSS string) instead of erroring -- which is
  // why active/open tree-item contrast looked broken. Normalize once here instead of touching
  // every call site.
  const colors = useMemo(() => {
    const c = themeColors || {};
    const pick = (flatKey, nested, fallback) =>
      typeof c[flatKey] === 'string' ? c[flatKey] : (nested || fallback);
    return {
      ...c,
      accent: pick('accent', c.primary?.main, '#3b82f6'),
      accentSubtle: pick('accentSubtle', c.primary?.subtle, 'rgba(59, 130, 246, 0.15)'),
      textPrimary: pick('textPrimary', c.text?.primary, '#f4f4f5'),
      textSecondary: pick('textSecondary', c.text?.secondary, '#d4d4d8'),
      textMuted: pick('textMuted', c.text?.muted, '#a1a1aa'),
      border: pick('border', c.border?.default, '#3f3f46'),
      warning: pick('warning', c.warning?.main, '#f59e0b'),
      info: pick('info', c.info?.main, '#3b82f6'),
      success: pick('success', c.success?.main, '#22c55e'),
      error: pick('error', c.error?.main, '#ef4444'),
    };
  }, [themeColors]);
  // Matches backend UserPrincipal.isPlatformAdmin() exactly (hasRole("FOYSONIS_DEV")) --
  // a base/product-level script (tenantId and connectedSystemId both null) is
  // shared across every tenant, so only a platform admin may delete it.
  const isPlatformAdmin = hasRole('FOYSONIS_DEV');

  // Tabs state
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);

  // Scripts list state
  const [scripts, setScripts] = useState([]);
  const [scriptsLoading, setScriptsLoading] = useState(true);

  // Script explorer state
  const [scriptTree, setScriptTree] = useState(null);
  const [explorerOpen, setExplorerOpen] = useState(true);
  const [explorerWidth, setExplorerWidth] = useState(240);
  const [isDraggingExplorer, setIsDraggingExplorer] = useState(false);
  const [expandedDomains, setExpandedDomains] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [explorerSearch, setExplorerSearch] = useState('');
  const [debouncedExplorerSearch, setDebouncedExplorerSearch] = useState('');
  const [searchIncludeCode, setSearchIncludeCode] = useState(false);
  const explorerSearchTimeoutRef = useRef(null);

  // UI state
  const [scriptMenuAnchor, setScriptMenuAnchor] = useState(null);
  const [contextPanelHeight, setContextPanelHeight] = useState(150);
  const [outputPanelHeight, setOutputPanelHeight] = useState(180);
  const [rightPanelWidth, setRightPanelWidth] = useState(350);
  const [isDraggingContext, setIsDraggingContext] = useState(false);
  const [isDraggingOutput, setIsDraggingOutput] = useState(false);
  const [isDraggingHorizontal, setIsDraggingHorizontal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Right panel state
  const [rightPanelOpen, setRightPanelOpen] = useState(true);

  // Save dialog state
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveForm, setSaveForm] = useState({ name: '', displayName: '', description: '', domain: '', category: '' });
  const [saveError, setSaveError] = useState(null);

  // Run menu state
  const [runMenuAnchor, setRunMenuAnchor] = useState(null);

  // Script search state
  const [scriptSearchQuery, setScriptSearchQuery] = useState('');

  // Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importedScript, setImportedScript] = useState(null); // { meta, scriptBody, fileName }
  const [importMatches, setImportMatches] = useState([]); // existing scripts with same name
  const [importMode, setImportMode] = useState('update'); // 'update' | 'create'
  const [importSelectedId, setImportSelectedId] = useState(null); // selected match to update
  const [importCreateForm, setImportCreateForm] = useState({ name: '', displayName: '', description: '', domain: '', category: '' });
  const [importLoading, setImportLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Sync from files panel state
  const [syncFromFilesOpen, setSyncFromFilesOpen] = useState(false);
  // Whether the current user can see/use "Sync from Files" -- resolved via
  // the real role_permissions model (ACTION target type), not a hardcoded
  // role check. Broken in any deployed environment today (see
  // project_file_sync_deployed_envs), gated to FOYSONIS_DEV via a
  // role_permissions row (db/10_schema/99_role_permissions_extend_actions.sql)
  // until it's properly rebuilt -- but that requirement lives in the data,
  // not here, so it can change without a frontend deploy.
  const [canSyncFromFiles, setCanSyncFromFiles] = useState(false);

  useEffect(() => {
    let cancelled = false;
    PermissionService.canAccess('ACTION', 'flex.sync_from_files').then((allowed) => {
      if (!cancelled) setCanSyncFromFiles(allowed);
    });
    return () => { cancelled = true; };
  }, []);

  // Delete script state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReferences, setDeleteReferences] = useState([]);
  const [deleting, setDeleting] = useState(false);

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null); // { mouseX, mouseY, script }

  // Folder (scope/domain/category) context menu state -- separate from the
  // script leaf context menu above since the action set differs per level.
  const [folderContextMenu, setFolderContextMenu] = useState(null); // { mouseX, mouseY, level, scope, domainName, categoryName }

  // Edit properties dialog state
  const [propsDialogOpen, setPropsDialogOpen] = useState(false);
  const [propsScript, setPropsScript] = useState(null);
  const [propsForm, setPropsForm] = useState({ name: '', displayName: '', description: '', domain: '', category: '', isInvokable: false });
  const [propsSaving, setPropsSaving] = useState(false);
  const [propsError, setPropsError] = useState(null);

  // Duplicate-as-override dialog state -- prompts for tenant vs warehouse
  // (system) scope; the copy always keeps the source's name so it functions
  // as a real override, not an unrelated new script.
  const [duplicateDialog, setDuplicateDialog] = useState({ open: false, script: null });
  const [duplicating, setDuplicating] = useState(false);

  // Validation timeout ref for debouncing
  const validationTimeoutRef = useRef(null);
  const editorRef = useRef(null);

  // Get active tab
  const activeTab = tabs.find(t => t.id === activeTabId);

  // Which scripts participate in the extends/override chain, computed
  // client-side from the already-loaded flat script list -- no extra
  // backend call needed. Two distinct sets/icons per the two directions:
  // overrideScriptIds = "this script overrides/extends another" (either
  // explicitly via extendsScript, or implicitly by sharing a name with a
  // broader-scope script); baseScriptIds = "this script is itself overridden
  // by something else" (the other end of either of those same two links).
  const { overrideScriptIds, baseScriptIds } = useMemo(() => {
    const overrideIds = new Set();
    const baseIds = new Set();
    const byName = {};
    scripts.forEach((s) => {
      if (!s.name) return;
      (byName[s.name] = byName[s.name] || []).push(s);
    });
    const scopeRank = (s) => (s.connectedSystemId ? 2 : s.tenantId ? 1 : 0);
    scripts.forEach((s) => {
      // Explicit @extends
      if (s.extendsScript) {
        overrideIds.add(s.id);
        (byName[s.extendsScript] || []).forEach((t) => { if (t.id !== s.id) baseIds.add(t.id); });
      }
      // Implicit same-name, more-specific-scope override
      (byName[s.name] || []).forEach((other) => {
        if (other.id === s.id) return;
        if (scopeRank(other) < scopeRank(s)) {
          overrideIds.add(s.id);
          baseIds.add(other.id);
        }
      });
    });
    return { overrideScriptIds: overrideIds, baseScriptIds: baseIds };
  }, [scripts]);

  // Check if current user is the script owner
  const isScriptOwner = (script) => {
    if (!script) return false;
    if (!user) return true; // If no user context, allow (shouldn't happen)
    if (!script.createdBy) return true; // If no createdBy, allow (legacy scripts)
    // Compare user id or email with script's createdBy
    return script.createdBy === user.id ||
           script.createdBy === user.email ||
           script.createdBy === String(user.id);
  };

  // Base (product-level) scripts have no tenantId/connectedSystemId -- shared
  // across every tenant, so only a platform admin may edit or delete them.
  // A tenant user should Duplicate (see handleDuplicateScript) to create
  // their own override instead of editing the shared base in place.
  const isBaseScopeScript = (script) => !!script && !script.tenantId && !script.connectedSystemId;
  const canMutateScript = (script) => !isBaseScopeScript(script) || isPlatformAdmin;
  const canDeleteScript = canMutateScript;

  // Check if the script is editable (not locked, or locked by current user;
  // and not a base-scope script unless the current user is a platform admin)
  const isScriptEditable = (tab) => {
    if (!tab || !tab.script) return true; // New unsaved scripts are always editable
    if (!canMutateScript(tab.script)) return false;
    if (!tab.script.isLocked) return true; // Unlocked scripts are editable
    // Locked scripts are only editable by the owner
    return isScriptOwner(tab.script);
  };

  // Check if the active tab is editable
  const canEditActiveTab = isScriptEditable(activeTab);

  // Properties dialog: same base-scope rule applies to Save there too.
  const propsReadOnly = !!propsScript && !canMutateScript(propsScript);

  // Check if current user can toggle the lock (must be owner or no owner set)
  const canToggleLock = activeTab?.scriptId && isScriptOwner(activeTab?.script);

  // Is this a saved script?
  const isSavedScript = !!activeTab?.scriptId;
  const isFlexScript = !activeTab?.artifactType || activeTab?.artifactType === 'script';
  const isAgent = activeTab?.artifactType === 'agent';
  const isWorkspace = activeTab?.artifactType === 'workspace';
  const artifactLabel = isAgent ? 'Agent' : isWorkspace ? 'Workspace' : 'Command';

  // Filter scripts based on search query
  const filteredScripts = scripts.filter(script => {
    if (!scriptSearchQuery.trim()) return true;
    const query = scriptSearchQuery.toLowerCase();
    return (
      script.name?.toLowerCase().includes(query) ||
      script.description?.toLowerCase().includes(query)
    );
  });

  // Load scripts list and tree
  const loadScripts = useCallback(async () => {
    try {
      setScriptsLoading(true);
      const scopeParams = {
        tenantId: user?.tenant?.id,
        connectedSystemId: selectedConnectedSystem?.id,
      };
      const [data, treeData] = await Promise.all([
        FlexService.listScripts(scopeParams),
        FlexService.getScriptTree(scopeParams).catch(() => null),
      ]);
      // listScripts returns array directly or { scripts: [] }
      setScripts(Array.isArray(data) ? data : (data.scripts || []));
      if (treeData) {
        setScriptTree(treeData);
        // Auto-expand all domains on first load
        const expanded = {};
        (treeData.scopes || []).forEach(s => s.domains?.forEach(d => { expanded[`${s.scope}:${d.name}`] = true; }));
        setExpandedDomains(prev => Object.keys(prev).length === 0 ? expanded : prev);
      }
    } catch (err) {
      console.error('Failed to load scripts:', err);
    } finally {
      setScriptsLoading(false);
    }
  }, [user, selectedConnectedSystem]);

  useEffect(() => {
    loadScripts();
  }, [loadScripts]);

  // Debounce explorer search (300ms)
  useEffect(() => {
    if (explorerSearchTimeoutRef.current) {
      clearTimeout(explorerSearchTimeoutRef.current);
    }
    explorerSearchTimeoutRef.current = setTimeout(() => {
      setDebouncedExplorerSearch(explorerSearch);
    }, 300);
    return () => {
      if (explorerSearchTimeoutRef.current) {
        clearTimeout(explorerSearchTimeoutRef.current);
      }
    };
  }, [explorerSearch]);

  // Handle drag for resizing panels
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDraggingExplorer) {
        const container = document.getElementById('flex-main-container');
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const newWidth = e.clientX - containerRect.left;
          setExplorerWidth(Math.max(160, Math.min(newWidth, 400)));
        }
        return;
      }

      const rightPanel = document.getElementById('flex-right-panel');
      if (!rightPanel) return;
      const rect = rightPanel.getBoundingClientRect();

      if (isDraggingContext) {
        const newHeight = e.clientY - rect.top;
        setContextPanelHeight(Math.max(80, Math.min(newHeight, 300)));
      }
      if (isDraggingOutput) {
        const contextBottom = rect.top + contextPanelHeight + 4;
        const newHeight = e.clientY - contextBottom;
        setOutputPanelHeight(Math.max(80, Math.min(newHeight, 400)));
      }
      if (isDraggingHorizontal) {
        const container = document.getElementById('flex-main-container');
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const newWidth = containerRect.right - e.clientX;
          setRightPanelWidth(Math.max(250, Math.min(newWidth, 600)));
        }
      }
    };

    const handleMouseUp = () => {
      setIsDraggingContext(false);
      setIsDraggingOutput(false);
      setIsDraggingHorizontal(false);
      setIsDraggingExplorer(false);
    };

    if (isDraggingContext || isDraggingOutput || isDraggingHorizontal || isDraggingExplorer) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDraggingContext, isDraggingOutput, isDraggingHorizontal, isDraggingExplorer, contextPanelHeight]);

  // Add new empty tab
  const handleAddTab = useCallback(() => {
    const newTab = createNewTab();
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newTab.id);
    setScriptMenuAnchor(null);
  }, []);

  // Ref to always have current tabs for duplicate check
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  // Open script in new tab
  const handleOpenScript = useCallback(async (script) => {
    // Check if already open (use ref for current tabs)
    const existingTab = tabsRef.current.find(t => t.scriptId === script.id);
    if (existingTab) {
      setActiveTabId(existingTab.id);
      setScriptMenuAnchor(null);
      return;
    }

    try {
      let newTab;
      const artifactType = script.type || 'script';

      if (artifactType === 'agent') {
        // Load agent as raw file content
        const agentData = await FlexService.getAgent(script.id);
        const alreadyOpen = tabsRef.current.find(t => t.scriptId === agentData.id);
        if (alreadyOpen) { setActiveTabId(alreadyOpen.id); setScriptMenuAnchor(null); return; }
        newTab = createNewTab({
          id: agentData.id,
          name: agentData.name,
          displayName: agentData.displayName,
          scriptBody: agentData.content,
        });
        newTab.artifactType = 'agent';
        newTab.language = 'yaml';
      } else if (artifactType === 'workspace') {
        // Load workspace as raw file content
        const wsData = await FlexService.getWorkspace(script.id);
        const alreadyOpen = tabsRef.current.find(t => t.scriptId === wsData.id);
        if (alreadyOpen) { setActiveTabId(alreadyOpen.id); setScriptMenuAnchor(null); return; }
        newTab = createNewTab({
          id: wsData.id,
          name: wsData.name,
          displayName: wsData.displayName,
          scriptBody: wsData.content,
        });
        newTab.artifactType = 'workspace';
        newTab.language = 'json';
      } else {
        // Standard FLEX script
        const fullScript = await FlexService.getScript(script.id);
        const alreadyOpen = tabsRef.current.find(t => t.scriptId === fullScript.id);
        if (alreadyOpen) { setActiveTabId(alreadyOpen.id); setScriptMenuAnchor(null); return; }
        newTab = createNewTab(fullScript);
        newTab.artifactType = 'script';
      }

      setTabs(prev => [...prev, newTab]);
      setActiveTabId(newTab.id);
    } catch (err) {
      console.error('Failed to load artifact:', err);
    }
    setScriptMenuAnchor(null);
  }, []);

  // Deep-link support: auto-open a specific command when this workspace is
  // reached via a row's Edit action elsewhere in the app (FAST_CONFIG's
  // FLEX Commands tab uses this -- see the OPEN_URL action handler in
  // actionHandlerRegistry.js, and project_flex_commands_rename_and_studio_edit_link).
  // Runs once `scripts` has actually loaded (so the id lookup has something
  // to search), and only once per mount -- consumedInitialScriptIdRef stops
  // it from re-firing every time `scripts` refreshes afterward (e.g. after
  // a save elsewhere triggers loadScripts() again).
  const consumedInitialScriptIdRef = useRef(false);
  useEffect(() => {
    if (!initialScriptId || consumedInitialScriptIdRef.current) return;
    if (scriptsLoading || scripts.length === 0) return;
    const match = scripts.find(s => s.id === initialScriptId);
    consumedInitialScriptIdRef.current = true;
    if (match) {
      handleOpenScript(match);
    } else {
      console.warn(`FlexWorkspace: initialScriptId ${initialScriptId} not found among loaded commands`);
    }
  }, [initialScriptId, scripts, scriptsLoading, handleOpenScript]);

  // Close tab
  const handleCloseTab = useCallback((tabId, e) => {
    e?.stopPropagation();
    setTabs(prev => {
      const newTabs = prev.filter(t => t.id !== tabId);
      if (newTabs.length === 0) {
        setActiveTabId(null);
        return [];
      }
      if (activeTabId === tabId) {
        // Switch to adjacent tab
        const closingIndex = prev.findIndex(t => t.id === tabId);
        const newIndex = Math.min(closingIndex, newTabs.length - 1);
        setActiveTabId(newTabs[newIndex].id);
      }
      return newTabs;
    });
  }, [activeTabId]);

  // Close all clean (unmodified) tabs, keep dirty ones
  const handleCloseCleanTabs = useCallback(() => {
    setTabs(prev => {
      const dirtyTabs = prev.filter(t => t.isModified);
      if (dirtyTabs.length === 0) {
        setActiveTabId(null);
        return [];
      }
      // If active tab was clean, switch to first dirty tab
      if (!dirtyTabs.find(t => t.id === activeTabId)) {
        setActiveTabId(dirtyTabs[0].id);
      }
      return dirtyTabs;
    });
  }, [activeTabId]);

  // Update tab state
  const updateTab = useCallback((tabId, updates) => {
    setTabs(prev => prev.map(tab =>
      tab.id === tabId ? { ...tab, ...updates } : tab
    ));
  }, []);

  // Handle editor content change with debounced validation
  const handleEditorChange = useCallback((newContent) => {
    if (!activeTab) return;
    updateTab(activeTab.id, {
      content: newContent,
      isModified: newContent !== activeTab.originalContent,
    });

    // Debounce syntax validation (500ms delay)
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }
    validationTimeoutRef.current = setTimeout(async () => {
      if (newContent && newContent.trim()) {
        try {
          const result = await FlexService.parseScript(newContent);
          const diagnostics = (result.diagnostics || []).map(d => ({
            severity: d.type?.toLowerCase() === 'error' ? 8 : d.type?.toLowerCase() === 'warning' ? 4 : 2,
            message: d.message,
            startLineNumber: d.line || 1,
            startColumn: d.column || 1,
            endLineNumber: d.line || 1,
            endColumn: (d.column || 1) + 10,
          }));
          updateTab(activeTab.id, { diagnostics });
        } catch (err) {
          console.error('Syntax validation failed:', err);
        }
      } else {
        updateTab(activeTab.id, { diagnostics: [] });
      }
    }, 500);
  }, [activeTab, updateTab]);

  // Handle context change
  const handleContextChange = useCallback((newContext) => {
    if (!activeTab) return;
    updateTab(activeTab.id, { executionContext: newContext });
  }, [activeTab, updateTab]);

  // Handle save - opens dialog for new scripts
  const handleSave = useCallback(async () => {
    if (!activeTab) return;

    // Check if script is editable
    if (!isScriptEditable(activeTab)) {
      const msg = activeTab?.script && !canMutateScript(activeTab.script)
        ? 'This is a base (product-level) command -- only a platform admin can edit it. Use Duplicate to create your own override instead.'
        : 'This command is locked and can only be modified by its owner.';
      alert(msg);
      return;
    }

    if (activeTab.scriptId) {
      // Update existing artifact
      setSaving(true);
      try {
        if (activeTab.artifactType === 'agent') {
          await FlexService.updateAgent(activeTab.scriptId, activeTab.content);
        } else if (activeTab.artifactType === 'workspace') {
          await FlexService.updateWorkspace(activeTab.scriptId, activeTab.content);
        } else {
          await FlexService.updateScript(activeTab.scriptId, {
            scriptBody: activeTab.content,
          });
        }
        updateTab(activeTab.id, {
          originalContent: activeTab.content,
          isModified: false,
        });
      } catch (err) {
        console.error('Failed to save:', err);
        alert(`Failed to save: ${err.message}`);
      } finally {
        setSaving(false);
      }
    } else {
      // New script - open save dialog
      setSaveForm({ name: activeTab.name || '', displayName: '', description: '', domain: '', category: '' });
      setSaveError(null);
      setSaveDialogOpen(true);
    }
  }, [activeTab, updateTab]);

  // Handle save dialog confirm
  const handleSaveDialogConfirm = useCallback(async () => {
    if (!activeTab || !saveForm.name.trim()) {
      setSaveError('Command name is required');
      return;
    }
    if (!saveForm.domain.trim() || !saveForm.category.trim()) {
      setSaveError('Domain and category are required');
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const scriptName = saveForm.name.trim();
      // If displayName is empty, derive from script name by replacing underscores with spaces
      const displayName = saveForm.displayName.trim() || scriptName.replace(/_/g, ' ');
      const created = await FlexService.createScript({
        name: scriptName,
        displayName,
        description: saveForm.description.trim() || null,
        domain: saveForm.domain.trim() || null,
        category: saveForm.category.trim() || null,
        scriptBody: activeTab.content,
      });
      updateTab(activeTab.id, {
        script: created,
        scriptId: created.id,
        name: created.name,
        originalContent: activeTab.content,
        isModified: false,
      });
      setSaveDialogOpen(false);
      // Refresh scripts list
      await loadScripts();
    } catch (err) {
      console.error('Failed to save:', err);
      setSaveError(err.message || 'Failed to save command');
    } finally {
      setSaving(false);
    }
  }, [activeTab, saveForm, updateTab, loadScripts]);

  // Handle run with optional execution mode
  // mode: 'run' (default), 'tracked', 'background'
  const handleRun = useCallback(async (mode = 'run') => {
    if (!activeTab || activeTab.isRunning) return;

    // Close menu if open
    setRunMenuAnchor(null);

    updateTab(activeTab.id, { isRunning: true, output: null });

    try {
      // Build the execution request object matching FlexExecuteRequest
      const request = {
        scriptBody: activeTab.content,
        context: activeTab.executionContext || {},
        executionMode: mode,
        trace: !!activeTab.traceEnabled,
      };
      // If script is saved, include scriptId and name
      if (activeTab.scriptId) {
        request.scriptId = activeTab.scriptId;
        request.scriptName = activeTab.name;
      }

      const result = await FlexService.executeScript(request);

      // Handle background execution (returns immediately with taskId)
      if (result.isBackground && result.taskId) {
        const output = {
          success: true,
          taskId: result.taskId,
          taskStatus: result.taskStatus,
          isBackground: true,
          diagnostics: [],
          variables: {},
          trace: [{
            timestamp: Date.now(),
            message: `Task started: ${result.taskId}. Check the Tasks workspace for progress.`,
            type: 'info',
          }],
        };
        updateTab(activeTab.id, { output, isRunning: false });
        return;
      }

      // @trace / "Enable Trace" emits INFO diagnostics prefixed "TRACE: " (one per
      // executed statement) -- pull those into the dedicated Trace panel instead of
      // mixing them into the Diagnostics list with real warnings/errors.
      const allDiagnostics = result.diagnostics || [];
      const traceDiagnostics = allDiagnostics.filter(d => d.message?.startsWith('TRACE: '));
      const nonTraceDiagnostics = allDiagnostics.filter(d => !d.message?.startsWith('TRACE: '));

      // Build output object in the format FlexOutputConsole expects
      const output = {
        success: result.success,
        processingTimeMs: result.executionTimeMs,
        taskId: result.taskId || null,
        taskStatus: result.taskStatus || null,
        // Map diagnostics to expected format (severity instead of type)
        diagnostics: nonTraceDiagnostics.map(d => ({
          severity: d.type?.toLowerCase() || 'info',
          message: d.message,
          line: d.line,
          column: d.column,
        })),
        // Filter out internal variables starting with __
        variables: result.context ? Object.fromEntries(
          Object.entries(result.context).filter(([key]) => !key.startsWith('__'))
        ) : {},
        // Trace panel: step-by-step TRACE diagnostics (when Enable Trace is on)
        // followed by notifications (Log() calls), in execution order.
        trace: [
          ...traceDiagnostics.map(d => ({
            timestamp: d.line || 0,
            message: d.message.replace(/^TRACE: /, ''),
            type: 'trace',
          })),
          ...(result.notifications || []).map((msg, i) => ({
            timestamp: i + 1,
            message: msg,
            type: 'info',
          })),
        ],
        // Error message if failed
        errorMessage: result.errorMessage,
      };

      // If there are variables, use them as the result too
      if (Object.keys(output.variables).length > 0) {
        output.result = output.variables;
      }

      // Add task info to trace if tracked
      if (result.taskId) {
        output.trace.unshift({
          timestamp: 0,
          message: `Task ID: ${result.taskId} (${result.taskStatus})`,
          type: 'info',
        });
      }

      updateTab(activeTab.id, { output, isRunning: false });
    } catch (err) {
      // Create error output object
      const output = {
        success: false,
        diagnostics: [{
          severity: 'error',
          message: err.message || 'Request failed',
          line: 0,
        }],
        variables: {},
      };
      updateTab(activeTab.id, { output, isRunning: false });
    }
  }, [activeTab, updateTab]);

  // Cleanup validation timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  // Toggle trace mode
  const handleToggleTrace = useCallback(() => {
    if (!activeTab) return;
    updateTab(activeTab.id, { traceEnabled: !activeTab.traceEnabled });
  }, [activeTab, updateTab]);

  // Toggle script lock (only owner can do this)
  const handleToggleLock = useCallback(async () => {
    if (!activeTab || !activeTab.scriptId) return;

    // Check if current user is the owner
    if (!isScriptOwner(activeTab.script)) {
      alert('Only the command owner can lock or unlock this command.');
      return;
    }

    try {
      const result = await FlexService.toggleLock(activeTab.scriptId);
      // Update the tab's script lock status
      updateTab(activeTab.id, {
        script: { ...activeTab.script, isLocked: result.isLocked },
      });
      // Refresh scripts list to show updated lock status
      await loadScripts();
    } catch (err) {
      console.error('Failed to toggle lock:', err);
      alert(`Failed to toggle lock: ${err.message}`);
    }
  }, [activeTab, updateTab, loadScripts]);

  // Delete script - show confirmation, then call endpoint which checks references
  const handleDeleteScript = useCallback(() => {
    if (!activeTab || !activeTab.scriptId) return;
    setDeleteReferences([]);
    setDeleteDialogOpen(true);
  }, [activeTab]);

  const handleConfirmDelete = useCallback(async () => {
    if (!activeTab || !activeTab.scriptId) return;
    if (!canDeleteScript(activeTab.script)) {
      alert('Only a platform admin can delete a base (product-level) command.');
      setDeleteDialogOpen(false);
      return;
    }
    setDeleting(true);
    try {
      let result;
      if (activeTab.artifactType === 'agent') {
        result = await FlexService.deleteAgent(activeTab.scriptId);
      } else if (activeTab.artifactType === 'workspace') {
        result = await FlexService.deleteWorkspace(activeTab.scriptId);
      } else {
        result = await FlexService.permanentDeleteScript(activeTab.scriptId);
      }
      if (result?.conflict) {
        setDeleteReferences(result.referencedBy);
        return;
      }
      // Close the tab
      const tabIdToClose = activeTab.id;
      setTabs(prev => {
        const remaining = prev.filter(t => t.id !== tabIdToClose);
        setActiveTabId(remaining.length > 0 ? remaining[remaining.length - 1].id : null);
        return remaining;
      });
      setDeleteDialogOpen(false);
      await loadScripts();
    } catch (err) {
      alert('Delete failed: ' + err.message);
    } finally {
      setDeleting(false);
    }
  }, [activeTab, loadScripts]);

  // Context menu handlers
  const handleContextMenu = useCallback((e, script) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, script });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Folder-level (scope/domain/category) context menu handlers
  const handleFolderContextMenu = useCallback((e, level, scope, domainName = null, categoryName = null) => {
    e.preventDefault();
    e.stopPropagation();
    setFolderContextMenu({ mouseX: e.clientX, mouseY: e.clientY, level, scope, domainName, categoryName });
  }, []);

  const handleCloseFolderContextMenu = useCallback(() => {
    setFolderContextMenu(null);
  }, []);

  // Collapse every domain (and their categories) under a scope
  const handleCollapseScope = useCallback((scope) => {
    setExpandedDomains(prev => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => { if (k.startsWith(`${scope}:`)) next[k] = false; });
      return next;
    });
    setExpandedCategories(prev => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => { if (k.startsWith(`${scope}:`)) next[k] = false; });
      return next;
    });
    setFolderContextMenu(null);
  }, []);

  // Collapse a single domain (and its categories)
  const handleCollapseDomain = useCallback((scope, domainName) => {
    const domainKey = `${scope}:${domainName}`;
    setExpandedDomains(prev => ({ ...prev, [domainKey]: false }));
    setExpandedCategories(prev => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => { if (k.startsWith(`${domainKey}/`)) next[k] = false; });
      return next;
    });
    setFolderContextMenu(null);
  }, []);

  // "Add New Domain" / "Add New Category" / "Add New Script" all funnel into
  // the same new-script flow: domain/category aren't real entities (the tree
  // just groups scripts by those two free-text columns), so "adding" one
  // just means opening the new-script dialog with domain/category pre-seeded
  // from wherever in the tree the action was triggered. Fields stay editable
  // (not locked) so the user can still redirect if needed.
  const handleAddScriptTo = useCallback((domain = '', category = '') => {
    const newTab = createNewTab();
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newTab.id);
    setSaveForm({ name: '', displayName: '', description: '', domain, category });
    setSaveError(null);
    setSaveDialogOpen(true);
    setFolderContextMenu(null);
  }, []);

  const handleOpenProperties = useCallback((script) => {
    setPropsScript(script);
    setPropsForm({
      name: script.name || '',
      displayName: script.displayName || '',
      description: script.description || '',
      domain: script.domain || '',
      category: script.category || '',
      isInvokable: script.isInvokable || false,
    });
    setPropsError(null);
    setPropsDialogOpen(true);
    setContextMenu(null);
  }, []);

  const handleSaveProperties = useCallback(async () => {
    if (!propsScript) return;
    if (!canMutateScript(propsScript)) {
      alert('Only a platform admin can edit a base (product-level) command. Use Duplicate to create your own override instead.');
      return;
    }
    if (!propsForm.domain.trim() || !propsForm.category.trim()) {
      setPropsError('Domain and category are required');
      return;
    }
    setPropsError(null);
    setPropsSaving(true);
    try {
      const updated = await FlexService.updateScript(propsScript.id, {
        ...propsScript,
        name: propsForm.name,
        displayName: propsForm.displayName,
        description: propsForm.description,
        domain: propsForm.domain,
        category: propsForm.category,
        isInvokable: propsForm.isInvokable,
      });
      // Update the tab if this script is open
      setTabs(prev => prev.map(t =>
        t.scriptId === propsScript.id
          ? { ...t, name: updated.name, script: { ...t.script, ...updated } }
          : t
      ));
      setPropsDialogOpen(false);
      await loadScripts();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setPropsSaving(false);
    }
  }, [propsScript, propsForm, loadScripts]);

  const handleDuplicateScript = useCallback((script) => {
    setContextMenu(null);
    if (script.type === 'agent' || script.type === 'workspace') {
      // Agents/workspaces aren't part of the tenant-override scope model --
      // keep the original "open an unsaved renamed copy" behavior for those.
      (async () => {
        try {
          const fetcher = script.type === 'agent' ? FlexService.getAgent : FlexService.getWorkspace;
          const data = await fetcher(script.id);
          const copyName = script.name + '_copy';
          const content = data.content.replace(
            /^(name:\s*)(.+)$/m,
            `$1${copyName}`
          );
          const newTab = createNewTab({ id: null, name: copyName, displayName: copyName, scriptBody: content });
          newTab.artifactType = script.type;
          newTab.language = script.type === 'agent' ? 'yaml' : 'json';
          setTabs(prev => [...prev, newTab]);
          setActiveTabId(newTab.id);
        } catch (err) {
          alert('Duplicate failed: ' + err.message);
        }
      })();
      return;
    }
    // Real FLEX scripts: always prompt for tenant/warehouse scope -- a
    // duplicate is only useful here as a same-named override (see
    // FlexScriptController.duplicate), never as another anonymous
    // product-level script.
    setDuplicateDialog({ open: true, script });
  }, []);

  const handleConfirmDuplicate = useCallback(async (scope) => {
    const script = duplicateDialog.script;
    if (!script) return;
    setDuplicating(true);
    try {
      const newScript = await FlexService.duplicateScript(script.id, scope);
      setDuplicateDialog({ open: false, script: null });
      await loadScripts();
      handleOpenScript(newScript);
    } catch (err) {
      alert('Duplicate failed: ' + err.message);
    } finally {
      setDuplicating(false);
    }
  }, [duplicateDialog, loadScripts, handleOpenScript]);

  const handleDeleteFromContext = useCallback((script) => {
    setContextMenu(null);
    // Open the script first if not already active, then trigger delete
    if (activeTab?.scriptId !== script.id) {
      handleOpenScript(script);
      // Small delay so the tab is set before delete dialog opens
      setTimeout(() => {
        setDeleteReferences([]);
        setDeleteDialogOpen(true);
      }, 200);
    } else {
      setDeleteReferences([]);
      setDeleteDialogOpen(true);
    }
  }, [activeTab, handleOpenScript]);

  // Clear output
  const handleClearOutput = useCallback(() => {
    if (!activeTab) return;
    updateTab(activeTab.id, { output: [] });
  }, [activeTab, updateTab]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isEditorFocused = document.activeElement?.closest('.monaco-editor');
      if (isEditorFocused) return;

      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
      if (e.key === 'F5' && (!activeTab?.artifactType || activeTab?.artifactType === 'script')) {
        e.preventDefault();
        handleRun('run');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave, handleRun]);

  // Warn before browser refresh/close if there are unsaved changes
  useEffect(() => {
    const hasDirtyTabs = tabs.some(t => t.isModified);
    if (!hasDirtyTabs) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [tabs]);

  // Search helper — spaces act as wildcards (match any characters)
  const matchesSearch = useCallback((text, query) => {
    if (!text || !query) return false;
    // Escape regex special chars, then replace spaces with .*
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = escaped.replace(/\s+/g, '.*');
    try {
      return new RegExp(pattern, 'i').test(text);
    } catch {
      return text.toLowerCase().includes(query.toLowerCase());
    }
  }, []);

  // Parse YAML frontmatter from .flex file content
  const parseFrontmatter = (fileContent) => {
    const lines = fileContent.split('\n');
    if (lines[0]?.trim() !== '---') {
      return { meta: {}, scriptBody: fileContent };
    }
    const endIndex = lines.findIndex((line, i) => i > 0 && line.trim() === '---');
    if (endIndex === -1) {
      return { meta: {}, scriptBody: fileContent };
    }
    const meta = {};
    for (let i = 1; i < endIndex; i++) {
      const line = lines[i];
      const colonIdx = line.indexOf(':');
      if (colonIdx > 0) {
        const key = line.substring(0, colonIdx).trim();
        let value = line.substring(colonIdx + 1).trim();
        // Remove surrounding quotes
        if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
          value = value.slice(1, -1).replace(/\\"/g, '"').replace(/\\n/g, '\n');
        }
        if (value === 'true') value = true;
        else if (value === 'false') value = false;
        meta[key] = value;
      }
    }
    const scriptBody = lines.slice(endIndex + 1).join('\n');
    return { meta, scriptBody };
  };

  // Handle file selection for import
  const handleImportFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset file input so same file can be re-selected
    e.target.value = '';

    try {
      const content = await file.text();
      const ext = file.name.split('.').pop().toLowerCase();

      // For .agent and .workspace files, open directly in editor as new unsaved tab
      if (ext === 'agent' || ext === 'workspace') {
        const { meta } = parseFrontmatter(content);
        const name = meta.name || file.name.replace(/\.(agent|workspace)$/, '');
        const newTab = createNewTab({ id: null, name, displayName: name, scriptBody: content });
        newTab.artifactType = ext === 'agent' ? 'agent' : 'workspace';
        newTab.language = ext === 'agent' ? 'yaml' : 'json';
        setTabs(prev => [...prev, newTab]);
        setActiveTabId(newTab.id);
        return;
      }

      const { meta, scriptBody } = parseFrontmatter(content);
      const scriptName = meta.name || file.name.replace(/\.flex$/, '');

      setImportedScript({ meta, scriptBody, fileName: file.name });

      // Search for existing scripts with matching name
      const allScripts = await FlexService.listScripts({ search: scriptName });
      const matches = (Array.isArray(allScripts) ? allScripts : (allScripts.scripts || []))
        .filter(s => s.name === scriptName);

      setImportMatches(matches);

      if (matches.length > 0) {
        setImportMode('update');
        setImportSelectedId(matches[0].id);
      } else {
        setImportMode('create');
      }

      // Pre-fill create form from frontmatter
      setImportCreateForm({
        name: scriptName,
        displayName: meta.displayName || scriptName.replace(/_/g, ' '),
        description: meta.description || '',
        domain: meta.domain || '',
        category: meta.category || '',
      });

      setImportDialogOpen(true);
    } catch (err) {
      console.error('Failed to read import file:', err);
      alert('Failed to read file: ' + err.message);
    }
  }, []);

  // Handle import confirm
  const handleImportConfirm = useCallback(async () => {
    if (!importedScript) return;
    setImportLoading(true);

    try {
      let result;
      if (importMode === 'update' && importSelectedId) {
        // Update existing script
        result = await FlexService.updateScript(importSelectedId, {
          scriptBody: importedScript.scriptBody,
        });
      } else {
        // Create new script
        const name = importCreateForm.name.trim();
        if (!name) {
          alert('Command name is required');
          setImportLoading(false);
          return;
        }
        result = await FlexService.createScript({
          name,
          displayName: importCreateForm.displayName.trim() || name.replace(/_/g, ' '),
          description: importCreateForm.description.trim() || null,
          domain: importCreateForm.domain.trim() || null,
          category: importCreateForm.category.trim() || null,
          scriptBody: importedScript.scriptBody,
        });
      }

      // Open the imported/updated script in a new tab
      const fullScript = await FlexService.getScript(result.id);
      const existingTab = tabs.find(t => t.scriptId === result.id);
      if (existingTab) {
        updateTab(existingTab.id, {
          script: fullScript,
          content: fullScript.scriptBody || '',
          originalContent: fullScript.scriptBody || '',
          isModified: false,
          name: fullScript.name,
        });
        setActiveTabId(existingTab.id);
      } else {
        const newTab = createNewTab(fullScript);
        setTabs(prev => [...prev, newTab]);
        setActiveTabId(newTab.id);
      }

      setImportDialogOpen(false);
      await loadScripts();
    } catch (err) {
      console.error('Import failed:', err);
      alert('Import failed: ' + err.message);
    } finally {
      setImportLoading(false);
    }
  }, [importedScript, importMode, importSelectedId, importCreateForm, tabs, updateTab, loadScripts]);

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: colors.background,
        overflow: 'hidden',
      }}
    >
      {/* Main Content */}
      <Box id="flex-main-container" sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Script Explorer Sidebar */}
          {explorerOpen && (
            <>
            <Box
              sx={{
                width: explorerWidth,
                minWidth: 160,
                maxWidth: 400,
                bgcolor: colors.surface,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {/* Explorer Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  borderBottom: `1px solid ${colors.border}`,
                  bgcolor: colors.surfaceElevated,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, flex: 1, color: colors.textPrimary, fontSize: 11 }}>
                  FLEX
                </Typography>
                <Tooltip title="New Command">
                  <IconButton size="small" onClick={handleAddTab} sx={{ p: 0.25 }}>
                    <AddIcon sx={{ fontSize: 14, color: colors.textSecondary }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reload commands from database">
                  <IconButton
                    size="small"
                    onClick={loadScripts}
                    disabled={scriptsLoading}
                    sx={{ p: 0.25 }}
                  >
                    <RefreshIcon sx={{ fontSize: 14, color: colors.textSecondary, '&:hover': { color: colors.success } }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export All">
                  <IconButton
                    size="small"
                    onClick={async () => {
                      for (const script of scripts) {
                        if (script.scriptBody) {
                          await FlexService.exportScript(script);
                        }
                      }
                    }}
                    sx={{ p: 0.25 }}
                  >
                    <ExportIcon sx={{ fontSize: 14, color: colors.textSecondary }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Sync to Files (save all commands to file system)">
                  <IconButton
                    size="small"
                    onClick={async () => {
                      try {
                        const result = await FlexService.syncToFiles();
                        alert(`Sync complete: ${result.written} commands written, ${result.skipped} skipped${result.errors?.length ? ', ' + result.errors.length + ' errors' : ''}.\n\nOutput: ${result.outputDir}`);
                      } catch (err) {
                        alert('Sync failed: ' + err.message);
                      }
                    }}
                    sx={{ p: 0.25 }}
                  >
                    <SyncIcon sx={{ fontSize: 14, color: colors.textSecondary }} />
                  </IconButton>
                </Tooltip>
                {canSyncFromFiles && (
                  <Tooltip title="Sync from Files (import commands from file system)">
                    <IconButton
                      size="small"
                      onClick={() => setSyncFromFilesOpen(true)}
                      sx={{ p: 0.25 }}
                    >
                      <SyncFromIcon sx={{ fontSize: 14, color: colors.textSecondary }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Import .flex file">
                  <IconButton
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ p: 0.25 }}
                  >
                    <ImportIcon sx={{ fontSize: 14, color: colors.textSecondary }} />
                  </IconButton>
                </Tooltip>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".flex,.agent,.workspace"
                  style={{ display: 'none' }}
                  onChange={handleImportFile}
                />
              </Box>

              {/* Explorer Search */}
              <Box sx={{ px: 1, py: 0.5 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search scripts..."
                  value={explorerSearch}
                  onChange={(e) => setExplorerSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 14, color: colors.textMuted }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputBase-root': { height: 28, fontSize: 11 },
                    '& .MuiInputBase-input': { py: 0.25, color: colors.textPrimary },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={searchIncludeCode}
                      onChange={(e) => setSearchIncludeCode(e.target.checked)}
                      size="small"
                      sx={{ p: 0.25, color: colors.textMuted, '&.Mui-checked': { color: colors.accent } }}
                    />
                  }
                  label="Code"
                  sx={{
                    ml: 0,
                    mt: 0.25,
                    '& .MuiFormControlLabel-label': { fontSize: 10, color: colors.textMuted },
                  }}
                />
              </Box>

              {/* Explorer Tree */}
              <Box sx={{ flex: 1, overflow: 'auto', px: 0.5, py: 0.5 }}>
                {scriptsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : (scriptTree?.scopes?.length || scriptTree?.domains) ? (
                  <>
                  {/* ── COMMANDS section ── */}
                  <Typography variant="caption" sx={{ px: 0.5, py: 0.5, display: 'block', fontWeight: 700, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: colors.textMuted }}>
                    Commands
                  </Typography>
                  {(scriptTree.scopes || [{ scope: 'product', label: null, domains: scriptTree.domains }]).map((scopeNode) => {
                    if (!scopeNode.domains?.length) return null;
                    const scopeIcon = scopeNode.scope === 'system' ? '🏭' : scopeNode.scope === 'tenant' ? '🏢' : '🌐';
                    return (
                    <Box key={scopeNode.scope}>
                      {scopeNode.label && (
                        <Box
                          onContextMenu={(e) => handleFolderContextMenu(e, 'scope', scopeNode.scope)}
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5, pt: 1, pb: 0.25 }}
                        >
                          <Typography sx={{ fontSize: 10 }}>{scopeIcon}</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 9, letterSpacing: '0.05em', textTransform: 'uppercase', color: colors.textMuted }}>
                            {scopeNode.label}{scopeNode.scope === 'product' ? ' (base)' : scopeNode.scope === 'tenant' ? ' (tenant)' : ' (warehouse)'}
                          </Typography>
                        </Box>
                      )}
                  {scopeNode.domains.map((domain) => {
                    const domainKey = `${scopeNode.scope}:${domain.name}`;
                    const isDomainExpanded = expandedDomains[domainKey] ?? false;
                    return (
                      <Box key={domainKey}>
                        {/* Domain node */}
                        <Box
                          onClick={() => setExpandedDomains(prev => ({ ...prev, [domainKey]: !prev[domainKey] }))}
                          onContextMenu={(e) => handleFolderContextMenu(e, 'domain', scopeNode.scope, domain.name)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            py: 0.25,
                            px: 0.5,
                            cursor: 'pointer',
                            borderRadius: 0.5,
                            '&:hover': { bgcolor: colors.surfaceElevated },
                          }}
                        >
                          {isDomainExpanded ? (
                            <ExpandMoreIcon sx={{ fontSize: 14, color: colors.textMuted }} />
                          ) : (
                            <ChevronRightIcon sx={{ fontSize: 14, color: colors.textMuted }} />
                          )}
                          {isDomainExpanded ? (
                            <FolderOpenIcon sx={{ fontSize: 14, color: colors.warning }} />
                          ) : (
                            <FolderIcon sx={{ fontSize: 14, color: colors.warning }} />
                          )}
                          <Typography variant="caption" sx={{ fontWeight: 600, flex: 1, color: colors.textPrimary, fontSize: 11 }}>
                            {domain.name}
                          </Typography>
                          <Chip label={domain.count} size="small" sx={{ height: 16, fontSize: 9, bgcolor: colors.surfaceElevated }} />
                        </Box>

                        {/* Categories */}
                        <Collapse in={isDomainExpanded}>
                          <Box sx={{ pl: 1 }}>
                            {domain.categories?.map((cat) => {
                              const catKey = `${domainKey}/${cat.name}`;
                              const isCatExpanded = expandedCategories[catKey] ?? false;
                              // Filter scripts by debounced search
                              const catScripts = debouncedExplorerSearch
                                ? cat.scripts.filter(s => {
                                    const q = debouncedExplorerSearch;
                                    return matchesSearch(s.name, q) ||
                                      matchesSearch(s.displayName, q) ||
                                      matchesSearch(s.description, q) ||
                                      (searchIncludeCode && matchesSearch(s.scriptBody, q));
                                  })
                                : cat.scripts;
                              if (debouncedExplorerSearch && catScripts.length === 0) return null;

                              return (
                                <Box key={cat.name}>
                                  {/* Category node */}
                                  <Box
                                    onClick={() => setExpandedCategories(prev => ({ ...prev, [catKey]: !prev[catKey] }))}
                                    onContextMenu={(e) => handleFolderContextMenu(e, 'category', scopeNode.scope, domain.name, cat.name)}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      py: 0.25,
                                      px: 0.5,
                                      cursor: 'pointer',
                                      borderRadius: 0.5,
                                      '&:hover': { bgcolor: colors.surfaceElevated },
                                    }}
                                  >
                                    {isCatExpanded ? (
                                      <ExpandMoreIcon sx={{ fontSize: 12, color: colors.textMuted }} />
                                    ) : (
                                      <ChevronRightIcon sx={{ fontSize: 12, color: colors.textMuted }} />
                                    )}
                                    <FolderIcon sx={{ fontSize: 13, color: colors.info }} />
                                    <Typography variant="caption" sx={{ flex: 1, color: colors.textSecondary, fontSize: 10 }}>
                                      {cat.name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: 9 }}>
                                      {catScripts.length}
                                    </Typography>
                                  </Box>

                                  {/* Scripts */}
                                  <Collapse in={isCatExpanded || !!debouncedExplorerSearch}>
                                    <Box sx={{ pl: 1.5 }}>
                                      {catScripts.map((script) => {
                                        const isActive = activeTab?.scriptId === script.id;
                                        const isOpen = !isActive && tabs.some(t => t.scriptId === script.id);
                                        return (
                                          <Tooltip
                                            key={script.id}
                                            title={script.description || ''}
                                            placement="right"
                                            arrow
                                            enterDelay={400}
                                          >
                                          <Box
                                            onClick={() => handleOpenScript(script)}
                                            onContextMenu={(e) => handleContextMenu(e, script)}
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 0.5,
                                              py: 0.25,
                                              px: 0.5,
                                              cursor: 'pointer',
                                              borderRadius: 0.5,
                                              borderLeft: isActive ? `3px solid ${colors.accent}` : '3px solid transparent',
                                              bgcolor: isActive ? colors.accent + '33' : isOpen ? colors.surfaceElevated : 'transparent',
                                              '&:hover': { bgcolor: isActive ? colors.accent + '44' : colors.surfaceElevated },
                                            }}
                                          >
                                            {script.isLocked ? (
                                              <LockIcon sx={{ fontSize: 11, color: colors.warning }} />
                                            ) : (
                                              <ScriptIcon sx={{ fontSize: 11, color: isActive ? colors.accent : isOpen ? colors.textPrimary : colors.textMuted }} />
                                            )}
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                flex: 1,
                                                fontSize: 10,
                                                color: isActive ? colors.accent : isOpen ? colors.textPrimary : colors.textSecondary,
                                                fontWeight: isActive ? 600 : isOpen ? 500 : 400,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                              }}
                                            >
                                              {script.name}
                                            </Typography>
                                            {overrideScriptIds.has(script.id) && (
                                              <Tooltip title="Overrides/extends another script" arrow>
                                                <OverrideIcon sx={{ fontSize: 11, color: colors.info }} />
                                              </Tooltip>
                                            )}
                                            {baseScriptIds.has(script.id) && (
                                              <Tooltip title="Overridden by a more specific script elsewhere" arrow>
                                                <OverriddenIcon sx={{ fontSize: 11, color: colors.warning }} />
                                              </Tooltip>
                                            )}
                                            {script.isInvokable && (
                                              <Tooltip title="Invokable -- a legal @invoke dynamic-dispatch target" arrow>
                                                <InvokableIcon sx={{ fontSize: 11, color: colors.success }} />
                                              </Tooltip>
                                            )}
                                          </Box>
                                          </Tooltip>
                                        );
                                      })}
                                    </Box>
                                  </Collapse>
                                </Box>
                              );
                            })}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                    </Box>
                    );
                  })}

                  {/* ── AGENTS section ── */}
                  {scriptTree.agents && scriptTree.agents.length > 0 && (
                    <>
                      <Box sx={{ mt: 1.5, mb: 0.5, borderTop: `1px solid ${colors.border}`, pt: 1 }}>
                        <Typography variant="caption" sx={{ px: 0.5, fontWeight: 700, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: colors.textMuted }}>
                          Agents
                        </Typography>
                      </Box>
                      {scriptTree.agents.map((agentGroup) => {
                        const agentGroupKey = `agent_${agentGroup.name}`;
                        const isGroupExpanded = expandedDomains[agentGroupKey] ?? true;
                        return (
                          <Box key={agentGroupKey}>
                            <Box
                              onClick={() => setExpandedDomains(prev => ({ ...prev, [agentGroupKey]: !prev[agentGroupKey] }))}
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25, px: 0.5, cursor: 'pointer', borderRadius: 0.5, '&:hover': { bgcolor: colors.surfaceElevated } }}
                            >
                              {isGroupExpanded ? <ExpandMoreIcon sx={{ fontSize: 14, color: colors.textMuted }} /> : <ChevronRightIcon sx={{ fontSize: 14, color: colors.textMuted }} />}
                              <AgentIcon sx={{ fontSize: 14, color: '#a78bfa' }} />
                              <Typography variant="caption" sx={{ fontWeight: 600, flex: 1, color: colors.textPrimary, fontSize: 11 }}>{agentGroup.name}</Typography>
                              <Chip label={agentGroup.count} size="small" sx={{ height: 16, fontSize: 9, bgcolor: colors.surfaceElevated }} />
                            </Box>
                            <Collapse in={isGroupExpanded}>
                              <Box sx={{ pl: 2 }}>
                                {agentGroup.items?.map((agent) => (
                                  <Tooltip key={agent.id} title={agent.description || ''} placement="right" arrow enterDelay={400}>
                                    <Box
                                      onClick={() => handleOpenScript({ id: agent.id, name: agent.name, displayName: agent.displayName, type: 'agent' })}
                                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25, px: 0.5, cursor: 'pointer', borderRadius: 0.5, '&:hover': { bgcolor: colors.surfaceElevated } }}
                                    >
                                      <AgentIcon sx={{ fontSize: 11, color: '#a78bfa' }} />
                                      <Typography variant="caption" sx={{ flex: 1, fontSize: 10, color: colors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {agent.name}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                ))}
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })}
                    </>
                  )}

                  {/* ── WORKSPACES section ── */}
                  {scriptTree.workspaces && scriptTree.workspaces.length > 0 && (
                    <>
                      <Box sx={{ mt: 1.5, mb: 0.5, borderTop: `1px solid ${colors.border}`, pt: 1 }}>
                        <Typography variant="caption" sx={{ px: 0.5, fontWeight: 700, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: colors.textMuted }}>
                          Workspaces
                        </Typography>
                      </Box>
                      {scriptTree.workspaces.map((ws) => (
                        <Tooltip key={ws.id} title={ws.description || ''} placement="right" arrow enterDelay={400}>
                          <Box
                            onClick={() => handleOpenScript({ id: ws.id, name: ws.name, displayName: ws.displayName, type: 'workspace' })}
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25, px: 0.5, cursor: 'pointer', borderRadius: 0.5, '&:hover': { bgcolor: colors.surfaceElevated } }}
                          >
                            <WorkspaceIcon sx={{ fontSize: 13, color: '#22d3ee' }} />
                            <Typography variant="caption" sx={{ flex: 1, fontSize: 10, color: colors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {ws.name}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: 9, color: colors.textMuted }}>{ws.domain}</Typography>
                          </Box>
                        </Tooltip>
                      ))}
                    </>
                  )}
                  </>
                ) : (
                  // Fallback: flat list when tree is not available
                  scripts.filter(script => {
                    if (!debouncedExplorerSearch.trim()) return true;
                    const q = debouncedExplorerSearch;
                    return matchesSearch(script.name, q) ||
                      matchesSearch(script.displayName, q) ||
                      matchesSearch(script.description, q) ||
                      (searchIncludeCode && matchesSearch(script.scriptBody, q));
                  }).map((script) => {
                    const isActive = activeTab?.scriptId === script.id;
                    const isOpen = !isActive && tabs.some(t => t.scriptId === script.id);
                    return (
                    <Tooltip
                      key={script.id}
                      title={script.description || ''}
                      placement="right"
                      arrow
                      enterDelay={400}
                    >
                    <Box
                      onClick={() => handleOpenScript(script)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        py: 0.25,
                        px: 0.5,
                        cursor: 'pointer',
                        borderRadius: 0.5,
                        borderLeft: isActive ? `3px solid ${colors.accent}` : '3px solid transparent',
                        bgcolor: isActive ? colors.accent + '33' : isOpen ? colors.surfaceElevated : 'transparent',
                        '&:hover': { bgcolor: isActive ? colors.accent + '44' : colors.surfaceElevated },
                      }}
                    >
                      <ScriptIcon sx={{ fontSize: 12, color: isActive ? colors.accent : isOpen ? colors.textPrimary : colors.textMuted }} />
                      <Typography variant="caption" sx={{ fontSize: 10, color: isActive ? colors.accent : isOpen ? colors.textPrimary : colors.textSecondary, fontWeight: isActive ? 600 : isOpen ? 500 : 400 }}>
                        {script.name}
                      </Typography>
                    </Box>
                    </Tooltip>
                    );
                  })
                )}
              </Box>
            </Box>
            {/* Explorer Drag Handle */}
            <Box
              onMouseDown={() => setIsDraggingExplorer(true)}
              sx={{
                width: 4,
                cursor: 'col-resize',
                bgcolor: colors.border,
                '&:hover': { bgcolor: colors.warning },
                transition: 'background-color 0.2s',
                flexShrink: 0,
              }}
            />
            </>
          )}

          {/* Persistent collapse/expand edge button */}
          <Box
            onClick={() => setExplorerOpen(prev => !prev)}
            sx={{
              width: 16,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              bgcolor: colors.surfaceElevated,
              borderRight: `1px solid ${colors.border}`,
              '&:hover': { bgcolor: colors.surfaceHover },
              transition: 'background-color 0.2s',
            }}
          >
            <Tooltip title={explorerOpen ? 'Collapse Commands' : 'Expand Commands'} placement="right">
              {explorerOpen
                ? <ChevronLeftIcon sx={{ fontSize: 14, color: colors.textMuted }} />
                : <ChevronRightIcon sx={{ fontSize: 14, color: colors.textMuted }} />
              }
            </Tooltip>
          </Box>

          {/* Editor + Tab Bar Column */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Tab Bar */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: colors.surface,
                borderBottom: `1px solid ${colors.border}`,
                minHeight: 36,
              }}
            >
              {/* Tabs */}
              <Box sx={{ flex: 1, display: 'flex', overflow: 'auto' }}>
                {tabs.map((tab) => {
                  const isActive = tab.id === activeTabId;
                  return (
                    <Tooltip key={tab.id} title={tab.name} enterDelay={500} arrow>
                    <Box
                      onClick={() => setActiveTabId(tab.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1.5,
                        py: 0.75,
                        cursor: 'pointer',
                        bgcolor: isActive ? colors.background : 'transparent',
                        borderBottom: isActive ? `2px solid ${colors.accent}` : '2px solid transparent',
                        '&:hover': {
                          bgcolor: isActive ? colors.background : colors.surfaceHover,
                        },
                        minWidth: 0,
                        maxWidth: 200,
                      }}
                    >
                      {tab.artifactType === 'agent' ? (
                        <AgentIcon sx={{ fontSize: 14, color: isActive ? '#a78bfa' : colors.textMuted, flexShrink: 0 }} />
                      ) : tab.artifactType === 'workspace' ? (
                        <WorkspaceIcon sx={{ fontSize: 14, color: isActive ? '#22d3ee' : colors.textMuted, flexShrink: 0 }} />
                      ) : (
                        <ScriptIcon sx={{ fontSize: 14, color: isActive ? colors.accent : colors.textMuted, flexShrink: 0 }} />
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          color: isActive ? colors.textPrimary : colors.textSecondary,
                          fontSize: '0.75rem',
                          fontWeight: isActive ? 600 : 400,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flex: 1,
                        }}
                      >
                        {tab.name}
                        {tab.isModified && <span style={{ color: '#f59e0b' }}> •</span>}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => handleCloseTab(tab.id, e)}
                        sx={{ p: 0.25, color: colors.textMuted, '&:hover': { color: colors.textPrimary } }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                    </Tooltip>
                  );
                })}
              </Box>

              {/* Close All Clean Tabs */}
              {tabs.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5, borderLeft: `1px solid ${colors.border}` }}>
                  <Tooltip title="Close all saved tabs">
                    <IconButton
                      size="small"
                      onClick={handleCloseCleanTabs}
                      sx={{ color: colors.textMuted, '&:hover': { color: colors.warning } }}
                    >
                      <CloseAllIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>

          {activeTab ? (
          <>
          {/* Code Editor */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Editor Toolbar */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.5,
                bgcolor: colors.surfaceElevated,
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <Tooltip title={!canEditActiveTab ? 'Command is locked (read-only)' : 'Save (Ctrl+S)'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={handleSave}
                    disabled={saving || !activeTab.isModified || !canEditActiveTab}
                    sx={{
                      color: !canEditActiveTab
                        ? colors.error
                        : activeTab.isModified
                          ? colors.warning
                          : colors.textMuted
                    }}
                  >
                    {saving ? <CircularProgress size={16} /> : <SaveIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </span>
              </Tooltip>
              {/* Run Button with Dropdown — only for FLEX scripts */}
              {isFlexScript && <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title="Run (F5)">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleRun('run')}
                      disabled={activeTab.isRunning}
                      sx={{ color: colors.success, borderRadius: 0 }}
                    >
                      {activeTab.isRunning ? <CircularProgress size={16} /> : <RunIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Run Options">
                  <IconButton
                    size="small"
                    onClick={(e) => setRunMenuAnchor(e.currentTarget)}
                    disabled={activeTab.isRunning}
                    sx={{
                      color: colors.textMuted,
                      p: 0.25,
                      ml: -0.5,
                      '&:hover': { color: colors.success },
                    }}
                  >
                    <DropdownIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>}

              {/* Run Options Menu */}
              <Menu
                anchorEl={runMenuAnchor}
                open={Boolean(runMenuAnchor)}
                onClose={() => setRunMenuAnchor(null)}
                PaperProps={{
                  sx: {
                    bgcolor: colors.surface,
                    border: `1px solid ${colors.border}`,
                    minWidth: 200,
                  },
                }}
              >
                <MenuItem onClick={() => handleRun('run')}>
                  <ListItemIcon><RunIcon sx={{ color: colors.success, fontSize: 18 }} /></ListItemIcon>
                  <ListItemText
                    primary="Run"
                    secondary="Quick execution, no tracking"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption', sx: { color: colors.textMuted } }}
                  />
                </MenuItem>
                <MenuItem onClick={() => handleRun('tracked')}>
                  <ListItemIcon><TrackIcon sx={{ color: colors.info, fontSize: 18 }} /></ListItemIcon>
                  <ListItemText
                    primary="Run With Tracking"
                    secondary="Monitored in Tasks workspace"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption', sx: { color: colors.textMuted } }}
                  />
                </MenuItem>
                <Divider sx={{ borderColor: colors.border }} />
                <MenuItem onClick={() => handleRun('background')}>
                  <ListItemIcon><BackgroundIcon sx={{ color: colors.warning, fontSize: 18 }} /></ListItemIcon>
                  <ListItemText
                    primary="Run in Background"
                    secondary="Async execution, monitor in Tasks"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption', sx: { color: colors.textMuted } }}
                  />
                </MenuItem>
              </Menu>
              <Tooltip title={activeTab.traceEnabled ? 'Disable Trace' : 'Enable Trace'}>
                <IconButton
                  size="small"
                  onClick={handleToggleTrace}
                  sx={{ color: activeTab.traceEnabled ? colors.info : colors.textMuted }}
                >
                  <TraceIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: colors.border }} />
              <Tooltip title={`Export as .${isAgent ? 'agent' : isWorkspace ? 'workspace' : 'flex'} file`}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (activeTab) {
                        const ext = isAgent ? 'agent' : isWorkspace ? 'workspace' : 'flex';
                        const blob = new Blob([activeTab.content], { type: 'text/plain' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${activeTab.name}.${ext}`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }
                    }}
                    disabled={!activeTab?.content}
                    sx={{ color: colors.textMuted, '&:hover': { color: colors.info } }}
                  >
                    <ExportIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: colors.border }} />
              <Tooltip title="Reload commands from database">
                <IconButton
                  size="small"
                  onClick={loadScripts}
                  sx={{ color: colors.textMuted, '&:hover': { color: colors.success } }}
                >
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>

              <Box sx={{ flex: 1 }} />

              {/* Read-only indicator when locked by another user */}
              {activeTab.script?.isLocked && !canToggleLock && (
                <Chip
                  size="small"
                  icon={<LockIcon sx={{ fontSize: 12 }} />}
                  label="Read Only"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    bgcolor: colors.errorSubtle,
                    color: colors.error,
                    mr: 1,
                  }}
                />
              )}
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                {activeTab.name}
              </Typography>
              {isSavedScript && (
                <Tooltip title="Edit Properties">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenProperties(activeTab.script)}
                    sx={{ color: colors.textMuted, p: 0.25, '&:hover': { color: colors.accent } }}
                  >
                    <EditIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
              {/* Lock/Unlock button - show for all saved scripts */}
              {isSavedScript && (
                <Tooltip
                  title={
                    !canToggleLock
                      ? `Locked by ${activeTab.script?.createdBy || 'another user'} - only they can unlock`
                      : activeTab.script?.isLocked
                        ? 'Unlock Command'
                        : 'Lock Command'
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleToggleLock}
                      disabled={!canToggleLock && activeTab.script?.isLocked}
                      sx={{
                        color: activeTab.script?.isLocked
                          ? (canToggleLock ? colors.warning : colors.error)
                          : colors.textMuted,
                        '&:hover': {
                          color: activeTab.script?.isLocked
                            ? (canToggleLock ? colors.success : colors.error)
                            : colors.warning
                        },
                        '&.Mui-disabled': { color: colors.error },
                      }}
                    >
                      {activeTab.script?.isLocked ? (
                        <LockIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <UnlockIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {isSavedScript && (
                <Tooltip title={canDeleteScript(activeTab?.script) ? 'Delete Command' : 'Base (product-level) commands can only be deleted by a platform admin'}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleDeleteScript}
                      disabled={!canDeleteScript(activeTab?.script)}
                      sx={{ color: colors.textMuted, '&:hover': { color: colors.error } }}
                    >
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>

            {/* Monaco Editor */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <FlexEditor
                ref={editorRef}
                value={activeTab.content}
                path={`inmemory://flex-scripts/${activeTab.id}.flex`}
                onChange={handleEditorChange}
                onSave={handleSave}
                onRun={handleRun}
                diagnostics={activeTab.diagnostics || []}
                themeMode={themeId}
                readOnly={!canEditActiveTab}
                language={activeTab.language || null}
              />
            </Box>
          </Box>

          {/* Right Panel Toggle / Drag Bar */}
        </>
          ) : (
          /* Welcome View — shown when no tabs are open */
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              overflow: 'auto',
              bgcolor: colors.background,
            }}
          >
            {/* Header area */}
            <Box sx={{ pt: 4, pb: 2, textAlign: 'center' }}>
              <CodeIcon sx={{ fontSize: 48, color: colors.textMuted, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 0.5, fontWeight: 600 }}>
                Flex Workspace
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textMuted, textAlign: 'center', maxWidth: 400 }}>
                Open a script from the explorer, or create a new one to get started.
              </Typography>
            </Box>

            {/* Recent scripts */}
            {scripts.length > 0 && (
              <Box sx={{ width: '100%', maxWidth: 500, px: 4, pb: 4 }}>
                <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 1.5, fontSize: 13 }}>
                  Recent
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {scripts
                    .slice()
                    .sort((a, b) => {
                      const dateA = a.updatedAt || a.createdAt || '';
                      const dateB = b.updatedAt || b.createdAt || '';
                      return dateB.localeCompare(dateA);
                    })
                    .slice(0, 10)
                    .map((script) => (
                      <Box
                        key={script.id}
                        onClick={() => handleOpenScript(script)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          py: 0.75,
                          px: 1.5,
                          cursor: 'pointer',
                          borderRadius: 1,
                          border: `1px solid ${colors.border}`,
                          bgcolor: colors.surface,
                          '&:hover': { bgcolor: colors.surfaceElevated, borderColor: colors.accent },
                          transition: 'all 0.15s',
                        }}
                      >
                        {script.isLocked ? (
                          <LockIcon sx={{ fontSize: 14, color: colors.warning }} />
                        ) : (
                          <ScriptIcon sx={{ fontSize: 14, color: colors.textMuted }} />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ color: colors.textPrimary, fontSize: 12, fontWeight: 500, fontFamily: "'IBM Plex Mono', monospace" }}>
                            {script.name}
                          </Typography>
                          {script.description && (
                            <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                              {script.description}
                            </Typography>
                          )}
                        </Box>
                        {overrideScriptIds.has(script.id) && (
                          <Tooltip title="Overrides/extends another script" arrow>
                            <OverrideIcon sx={{ fontSize: 13, color: colors.info }} />
                          </Tooltip>
                        )}
                        {baseScriptIds.has(script.id) && (
                          <Tooltip title="Overridden by a more specific script elsewhere" arrow>
                            <OverriddenIcon sx={{ fontSize: 13, color: colors.warning }} />
                          </Tooltip>
                        )}
                        {script.isInvokable && (
                          <Tooltip title="Invokable -- a legal @invoke dynamic-dispatch target" arrow>
                            <InvokableIcon sx={{ fontSize: 13, color: colors.success }} />
                          </Tooltip>
                        )}
                        {(script.domain || script.category) && (
                          <Chip
                            label={[script.domain, script.category].filter(Boolean).join('/')}
                            size="small"
                            sx={{ height: 18, fontSize: 9, bgcolor: colors.surfaceElevated, color: colors.textSecondary }}
                          />
                        )}
                      </Box>
                    ))}
                </Box>
              </Box>
            )}
          </Box>
          )}
        </Box>

          {/* Right Panel Toggle/Resize */}
          {activeTab && rightPanelOpen ? (
            <Box
              onMouseDown={() => setIsDraggingHorizontal(true)}
              sx={{
                width: 4,
                cursor: 'col-resize',
                bgcolor: colors.border,
                '&:hover': { bgcolor: '#f59e0b' },
                transition: 'background-color 0.2s',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Tooltip title="Collapse panel" placement="left">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setRightPanelOpen(false); }}
                  sx={{
                    position: 'absolute', top: '50%', left: -10, transform: 'translateY(-50%)',
                    width: 20, height: 20, bgcolor: colors.surface,
                    border: `1px solid ${colors.border}`, borderRadius: '50%', zIndex: 1, p: 0,
                    '&:hover': { bgcolor: colors.surfaceHover },
                  }}
                >
                  <ChevronRightIcon sx={{ fontSize: 14, color: colors.textSecondary }} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : activeTab ? (
            <Box
              onClick={() => setRightPanelOpen(true)}
              sx={{
                width: 20, display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center',
                bgcolor: colors.surface, borderLeft: `1px solid ${colors.border}`,
                cursor: 'pointer',
                '&:hover': { bgcolor: colors.surfaceHover },
                transition: 'background-color 0.2s',
              }}
            >
              <Tooltip title="Expand Context & Output" placement="left">
                <ChevronLeftIcon sx={{ fontSize: 14, color: colors.textMuted }} />
              </Tooltip>
            </Box>
          ) : null}

          {/* Right Panel - Context & Output (sibling of editor column) */}
          {activeTab && rightPanelOpen && (
          <Box
            id="flex-right-panel"
            sx={{
              width: rightPanelWidth,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderLeft: `1px solid ${colors.border}`,
            }}
          >
            {/* Context Input Panel */}
            <Box sx={{ height: contextPanelHeight, minHeight: 80, overflow: 'hidden' }}>
              <FlexContextPanel
                context={activeTab.executionContext}
                onChange={handleContextChange}
                isCollapsed={false}
                onToggleCollapse={() => {}}
                embedded={true}
              />
            </Box>

            {/* Drag Bar */}
            <Box
              onMouseDown={() => setIsDraggingContext(true)}
              sx={{
                height: 4,
                cursor: 'row-resize',
                bgcolor: colors.border,
                '&:hover': { bgcolor: '#f59e0b' },
                flexShrink: 0,
              }}
            />

            {/* Output Console */}
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <FlexOutputConsole
                output={activeTab.output}
                isCollapsed={false}
                onToggleCollapse={() => {}}
                onClear={handleClearOutput}
                onGoToLine={(line, column) => editorRef.current?.goToLine(line, column)}
                embedded={true}
                traceEnabled={!!activeTab.traceEnabled}
              />
            </Box>
          </Box>
          )}
      </Box>

      {/* Save Script Dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 0,
            minWidth: 480,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.border}`, pb: 1.5 }}>
          Save Script
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {saveError && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: colors.errorSubtle, border: `1px solid ${colors.error}`, borderRadius: 0 }}>
              <Typography variant="body2" sx={{ color: colors.error }}>{saveError}</Typography>
            </Box>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Command Name"
            placeholder="e.g., calculate_inventory_totals"
            value={saveForm.name}
            onChange={(e) => setSaveForm(prev => ({ ...prev, name: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && handleSaveDialogConfirm()}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
              '& .MuiInputBase-input': { color: colors.textPrimary },
              '& .MuiInputLabel-root': { color: colors.textSecondary },
            }}
          />
          <TextField
            fullWidth
            label="Display Name"
            placeholder={saveForm.name ? saveForm.name.replace(/_/g, ' ') : 'e.g., Calculate Inventory Totals'}
            value={saveForm.displayName}
            onChange={(e) => setSaveForm(prev => ({ ...prev, displayName: e.target.value }))}
            helperText="Leave empty to auto-generate from script name"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
              '& .MuiInputBase-input': { color: colors.textPrimary },
              '& .MuiInputLabel-root': { color: colors.textSecondary },
              '& .MuiFormHelperText-root': { color: colors.textMuted, fontSize: 10 },
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Autocomplete
              freeSolo
              fullWidth
              options={scriptTree?.domains?.map(d => d.name).filter(n => n !== 'ungrouped') || []}
              value={saveForm.domain}
              onInputChange={(e, value) => setSaveForm(prev => ({ ...prev, domain: value || '' }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Domain"
                  placeholder="e.g., receiving, warehouse"
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
                    '& .MuiInputBase-input': { color: colors.textPrimary },
                    '& .MuiInputLabel-root': { color: colors.textSecondary },
                  }}
                />
              )}
              sx={{
                '& .MuiAutocomplete-popupIndicator': { color: colors.textMuted },
                '& .MuiAutocomplete-clearIndicator': { color: colors.textMuted },
              }}
              componentsProps={{
                paper: { sx: { bgcolor: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 0,
                  '& .MuiAutocomplete-option': { color: colors.textPrimary, '&:hover': { bgcolor: colors.hover } },
                } },
              }}
            />
            <Autocomplete
              freeSolo
              fullWidth
              options={
                saveForm.domain
                  ? (scriptTree?.domains?.find(d => d.name === saveForm.domain)?.categories?.map(c => c.name).filter(n => n !== 'ungrouped') || [])
                  : (scriptTree?.domains?.flatMap(d => d.categories?.map(c => c.name) || []).filter((v, i, a) => a.indexOf(v) === i && v !== 'ungrouped') || [])
              }
              value={saveForm.category}
              onInputChange={(e, value) => setSaveForm(prev => ({ ...prev, category: value || '' }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Category"
                  placeholder="e.g., setup, validation"
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
                    '& .MuiInputBase-input': { color: colors.textPrimary },
                    '& .MuiInputLabel-root': { color: colors.textSecondary },
                  }}
                />
              )}
              sx={{
                '& .MuiAutocomplete-popupIndicator': { color: colors.textMuted },
                '& .MuiAutocomplete-clearIndicator': { color: colors.textMuted },
              }}
              componentsProps={{
                paper: { sx: { bgcolor: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 0,
                  '& .MuiAutocomplete-option': { color: colors.textPrimary, '&:hover': { bgcolor: colors.hover } },
                } },
              }}
            />
          </Box>
          <TextField
            fullWidth
            label="Description (optional)"
            placeholder="Brief description of what this script does"
            multiline
            rows={2}
            value={saveForm.description}
            onChange={(e) => setSaveForm(prev => ({ ...prev, description: e.target.value }))}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
              '& .MuiInputBase-input': { color: colors.textPrimary },
              '& .MuiInputLabel-root': { color: colors.textSecondary },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.border}` }}>
          <Button
            onClick={() => setSaveDialogOpen(false)}
            sx={{ color: colors.textSecondary, borderRadius: 0 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveDialogConfirm}
            disabled={saving || !saveForm.name.trim() || !saveForm.domain.trim() || !saveForm.category.trim()}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={{ bgcolor: colors.success, borderRadius: 0, '&:hover': { bgcolor: colors.successDark || colors.success } }}
          >
            {saving ? 'Saving...' : 'Save Command'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Script Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            border: `1px solid ${colors.border}`,
            minWidth: 180,
            '& .MuiMenuItem-root': { fontSize: '0.8rem', py: 0.75, color: colors.text },
            '& .MuiListItemIcon-root': { minWidth: 28 },
          },
        }}
      >
        <MenuItem onClick={() => { handleOpenProperties(contextMenu?.script); }}>
          <ListItemIcon><EditIcon sx={{ fontSize: 16, color: colors.textSecondary }} /></ListItemIcon>
          <ListItemText>Properties</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleDuplicateScript(contextMenu?.script); }}>
          <ListItemIcon><DuplicateIcon sx={{ fontSize: 16, color: colors.textSecondary }} /></ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        {(!contextMenu?.script?.type || contextMenu?.script?.type === 'script') && (
        <MenuItem onClick={() => { handleOpenScript(contextMenu?.script); handleCloseContextMenu(); handleRun('run'); }}>
          <ListItemIcon><RunIcon sx={{ fontSize: 16, color: colors.success }} /></ListItemIcon>
          <ListItemText>Run</ListItemText>
        </MenuItem>
        )}
        <Divider sx={{ borderColor: colors.border }} />
        <MenuItem
          onClick={() => { handleDeleteFromContext(contextMenu?.script); }}
          disabled={!canDeleteScript(contextMenu?.script)}
        >
          <ListItemIcon><DeleteIcon sx={{ fontSize: 16, color: colors.error }} /></ListItemIcon>
          <ListItemText sx={{ '& .MuiTypography-root': { color: colors.error } }}>
            {canDeleteScript(contextMenu?.script) ? 'Delete' : 'Delete (platform admin only)'}
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Folder (Scope/Domain/Category) Context Menu */}
      <Menu
        open={folderContextMenu !== null}
        onClose={handleCloseFolderContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={folderContextMenu ? { top: folderContextMenu.mouseY, left: folderContextMenu.mouseX } : undefined}
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            border: `1px solid ${colors.border}`,
            minWidth: 190,
            '& .MuiMenuItem-root': { fontSize: '0.8rem', py: 0.75, color: colors.text },
            '& .MuiListItemIcon-root': { minWidth: 28 },
          },
        }}
      >
        {folderContextMenu?.level === 'scope' && (
          <MenuItem onClick={() => handleCollapseScope(folderContextMenu.scope)}>
            <ListItemIcon><CollapseIcon sx={{ fontSize: 16, color: colors.textSecondary }} /></ListItemIcon>
            <ListItemText>Collapse All</ListItemText>
          </MenuItem>
        )}
        {folderContextMenu?.level === 'domain' && [
          <MenuItem key="collapse" onClick={() => handleCollapseDomain(folderContextMenu.scope, folderContextMenu.domainName)}>
            <ListItemIcon><CollapseIcon sx={{ fontSize: 16, color: colors.textSecondary }} /></ListItemIcon>
            <ListItemText>Collapse</ListItemText>
          </MenuItem>,
          <Divider key="div" sx={{ borderColor: colors.border }} />,
          <MenuItem key="add-domain" onClick={() => handleAddScriptTo('', '')}>
            <ListItemIcon><AddFolderIcon sx={{ fontSize: 16, color: colors.textSecondary }} /></ListItemIcon>
            <ListItemText>Add New Domain</ListItemText>
          </MenuItem>,
          <MenuItem key="add-category" onClick={() => handleAddScriptTo(folderContextMenu.domainName, '')}>
            <ListItemIcon><AddFolderIcon sx={{ fontSize: 16, color: colors.textSecondary }} /></ListItemIcon>
            <ListItemText>Add New Category</ListItemText>
          </MenuItem>,
        ]}
        {folderContextMenu?.level === 'category' && (
          <MenuItem onClick={() => handleAddScriptTo(folderContextMenu.domainName, folderContextMenu.categoryName)}>
            <ListItemIcon><AddScriptIcon sx={{ fontSize: 16, color: colors.textSecondary }} /></ListItemIcon>
            <ListItemText>Add New Script</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Edit Properties Dialog */}
      <Dialog
        open={propsDialogOpen}
        onClose={() => setPropsDialogOpen(false)}
        PaperProps={{
          sx: { bgcolor: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 0, minWidth: 480 },
        }}
      >
        <DialogTitle sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.border}`, pb: 1.5 }}>
          {artifactLabel} Properties
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {propsReadOnly && (
            <Typography variant="caption" sx={{ display: 'block', mb: 2, color: colors.warning, fontWeight: 600 }}>
              This is a base (product-level) script -- only a platform admin can edit it.
              Use Duplicate instead to create your own tenant/warehouse override.
            </Typography>
          )}
          {propsError && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: colors.errorSubtle, border: `1px solid ${colors.error}`, borderRadius: 0 }}>
              <Typography variant="body2" sx={{ color: colors.error }}>{propsError}</Typography>
            </Box>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Command Name"
            value={propsForm.name}
            onChange={(e) => setPropsForm(prev => ({ ...prev, name: e.target.value }))}
            disabled={propsReadOnly}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
              '& .MuiInputBase-input': { color: colors.textPrimary },
              '& .MuiInputLabel-root': { color: colors.textSecondary },
            }}
          />
          <TextField
            fullWidth
            label="Display Name"
            placeholder={propsForm.name ? propsForm.name.replace(/_/g, ' ') : ''}
            value={propsForm.displayName}
            onChange={(e) => setPropsForm(prev => ({ ...prev, displayName: e.target.value }))}
            helperText="Leave empty to auto-generate from script name"
            disabled={propsReadOnly}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
              '& .MuiInputBase-input': { color: colors.textPrimary },
              '& .MuiInputLabel-root': { color: colors.textSecondary },
              '& .MuiFormHelperText-root': { color: colors.textMuted, fontSize: 10 },
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Autocomplete
              freeSolo
              fullWidth
              disabled={propsReadOnly}
              options={scriptTree?.domains?.map(d => d.name).filter(n => n !== 'ungrouped') || []}
              value={propsForm.domain}
              onInputChange={(e, value) => setPropsForm(prev => ({ ...prev, domain: value || '' }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Domain"
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
                    '& .MuiInputBase-input': { color: colors.textPrimary },
                    '& .MuiInputLabel-root': { color: colors.textSecondary },
                  }}
                />
              )}
              componentsProps={{
                paper: { sx: { bgcolor: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 0,
                  '& .MuiAutocomplete-option': { color: colors.textPrimary, '&:hover': { bgcolor: colors.hover } },
                } },
              }}
            />
            <Autocomplete
              freeSolo
              fullWidth
              disabled={propsReadOnly}
              options={
                propsForm.domain
                  ? (scriptTree?.domains?.find(d => d.name === propsForm.domain)?.categories?.map(c => c.name).filter(n => n !== 'ungrouped') || [])
                  : (scriptTree?.domains?.flatMap(d => d.categories?.map(c => c.name) || []).filter((v, i, a) => a.indexOf(v) === i && v !== 'ungrouped') || [])
              }
              value={propsForm.category}
              onInputChange={(e, value) => setPropsForm(prev => ({ ...prev, category: value || '' }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Category"
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
                    '& .MuiInputBase-input': { color: colors.textPrimary },
                    '& .MuiInputLabel-root': { color: colors.textSecondary },
                  }}
                />
              )}
              componentsProps={{
                paper: { sx: { bgcolor: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 0,
                  '& .MuiAutocomplete-option': { color: colors.textPrimary, '&:hover': { bgcolor: colors.hover } },
                } },
              }}
            />
          </Box>
          <TextField
            fullWidth
            label="Description"
            placeholder="Brief description of what this script does"
            multiline
            rows={2}
            value={propsForm.description}
            onChange={(e) => setPropsForm(prev => ({ ...prev, description: e.target.value }))}
            disabled={propsReadOnly}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
              '& .MuiInputBase-input': { color: colors.textPrimary },
              '& .MuiInputLabel-root': { color: colors.textSecondary },
            }}
          />

          {propsScript && (
            <>
              <Divider sx={{ my: 2, borderColor: colors.border }} />
              <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mb: 1 }}>
                Schema (auto-derived from @requires/@optional/@returns and, for
                output_schema, a real sample run -- not editable here; re-run
                the schema backfill to refresh)
              </Typography>
              <ReadOnlyJsonField label="Input Schema" value={propsScript?.inputSchema} />
              <ReadOnlyJsonField label="Output Schema" value={propsScript?.outputSchema} />

              <Divider sx={{ my: 2, borderColor: colors.border }} />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!propsForm.isInvokable}
                    onChange={(e) => setPropsForm(prev => ({ ...prev, isInvokable: e.target.checked }))}
                    disabled={propsReadOnly}
                    sx={{ color: colors.textMuted, '&.Mui-checked': { color: colors.success } }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ color: colors.textPrimary }}>Invokable</Typography>
                    <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>
                      Allows this script to be a dynamic-dispatch target of @invoke (target name resolved at
                      runtime, e.g. from a DB-driven config value). Off by default -- a script must opt in to be
                      reachable this way, since @invoke's target can be built from data outside this script's own
                      source.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mt: 0.5 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.border}` }}>
          <Button onClick={() => setPropsDialogOpen(false)} sx={{ color: colors.textSecondary, borderRadius: 0 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveProperties}
            disabled={propsSaving || !propsForm.name.trim() || !propsForm.domain.trim() || !propsForm.category.trim() || propsReadOnly}
            startIcon={propsSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={{ bgcolor: colors.success, borderRadius: 0, '&:hover': { bgcolor: colors.successDark || colors.success } }}
          >
            {propsSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate-as-Override Dialog -- always prompts for scope; the copy
          keeps the source's exact name so it functions as a real tenant/
          system override via the resolver's scope hierarchy, not an
          unrelated new script. */}
      <Dialog
        open={duplicateDialog.open}
        onClose={() => { if (!duplicating) setDuplicateDialog({ open: false, script: null }); }}
        PaperProps={{
          sx: { bgcolor: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 0, minWidth: 440 },
        }}
      >
        <DialogTitle sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.border}`, pb: 1.5 }}>
          Customize "{duplicateDialog.script?.name}"
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
            This creates your own copy with the same name, which overrides the
            original wherever it's called (@call, super(), entity actions).
            Choose where it applies:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              disabled={duplicating || !selectedConnectedSystem}
              onClick={() => handleConfirmDuplicate('system')}
              sx={{ justifyContent: 'flex-start', textTransform: 'none', borderRadius: 0, borderColor: colors.border, color: colors.textPrimary, py: 1.25 }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  This warehouse only{selectedConnectedSystem ? ` — ${selectedConnectedSystem.systemName || selectedConnectedSystem.systemCode}` : ''}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  {selectedConnectedSystem ? 'Applies only when this warehouse is active' : 'Select a warehouse first to enable this option'}
                </Typography>
              </Box>
            </Button>
            <Button
              fullWidth
              variant="outlined"
              disabled={duplicating}
              onClick={() => handleConfirmDuplicate('tenant')}
              sx={{ justifyContent: 'flex-start', textTransform: 'none', borderRadius: 0, borderColor: colors.border, color: colors.textPrimary, py: 1.25 }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>My tenant (all warehouses)</Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  Applies across every warehouse in your tenant, unless a
                  warehouse-specific override also exists
                </Typography>
              </Box>
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.border}` }}>
          <Button
            onClick={() => setDuplicateDialog({ open: false, script: null })}
            disabled={duplicating}
            sx={{ color: colors.textSecondary, borderRadius: 0 }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Script Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => { if (!deleting) setDeleteDialogOpen(false); }}
        PaperProps={{ sx: { bgcolor: colors.surface, color: colors.text, minWidth: 400 } }}
      >
        <DialogTitle sx={{ color: colors.text }}>
          {deleteReferences.length > 0 ? 'Cannot Delete Command' : 'Delete Command?'}
        </DialogTitle>
        <DialogContent>
          {deleteReferences.length > 0 ? (
            <Box>
              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>
                <strong>{activeTab?.name}</strong> is referenced by the following scripts:
              </Typography>
              <Box sx={{ bgcolor: colors.background, borderRadius: 1, p: 1 }}>
                {deleteReferences.map((ref, i) => (
                  <Typography key={i} variant="body2" sx={{ color: colors.error, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {ref}
                  </Typography>
                ))}
              </Box>
              <Typography variant="caption" sx={{ color: colors.textSecondary, mt: 1, display: 'block' }}>
                Remove all references before deleting this script.
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              Permanently delete <strong>{activeTab?.name}</strong>? This will remove it from the database and file system. This action cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
            sx={{ color: colors.textSecondary }}
          >
            {deleteReferences.length > 0 ? 'Close' : 'Cancel'}
          </Button>
          {deleteReferences.length === 0 && (
            <Button
              onClick={handleConfirmDelete}
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
              sx={{ color: '#f44336' }}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Sync From Files Panel */}
      {syncFromFilesOpen && (
        <SyncFromFilesPanel
          colors={colors}
          onClose={() => setSyncFromFilesOpen(false)}
          onSyncComplete={loadScripts}
        />
      )}

      {/* Import Script Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 0,
            minWidth: 480,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.border}`, pb: 1.5 }}>
          Import Script — {importedScript?.fileName}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {importMatches.length > 0 && (
            <>
              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1.5 }}>
                Found {importMatches.length} existing script{importMatches.length > 1 ? 's' : ''} with the name "{importedScript?.meta?.name}".
                Choose to update an existing script or create a new one.
              </Typography>

              {/* Mode toggle */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  size="small"
                  variant={importMode === 'update' ? 'contained' : 'outlined'}
                  onClick={() => setImportMode('update')}
                  sx={{
                    borderRadius: 0,
                    bgcolor: importMode === 'update' ? colors.accent : 'transparent',
                    color: importMode === 'update' ? '#fff' : colors.textSecondary,
                    borderColor: colors.border,
                    textTransform: 'none',
                    fontSize: 12,
                  }}
                >
                  Update Existing
                </Button>
                <Button
                  size="small"
                  variant={importMode === 'create' ? 'contained' : 'outlined'}
                  onClick={() => setImportMode('create')}
                  sx={{
                    borderRadius: 0,
                    bgcolor: importMode === 'create' ? colors.accent : 'transparent',
                    color: importMode === 'create' ? '#fff' : colors.textSecondary,
                    borderColor: colors.border,
                    textTransform: 'none',
                    fontSize: 12,
                  }}
                >
                  Create New
                </Button>
              </Box>

              {/* Update mode — select which script to update */}
              {importMode === 'update' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: colors.textMuted, mb: 0.5, display: 'block' }}>
                    Select the script to update:
                  </Typography>
                  {importMatches.map((match) => {
                    const scopeLabel = match.connectedSystemId
                      ? `System: ${match.connectedSystemId}`
                      : match.tenantId
                        ? `Tenant: ${match.tenantId}`
                        : 'Product';
                    return (
                      <Box
                        key={match.id}
                        onClick={() => setImportSelectedId(match.id)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1,
                          mb: 0.5,
                          cursor: 'pointer',
                          border: `1px solid ${importSelectedId === match.id ? colors.accent : colors.border}`,
                          bgcolor: importSelectedId === match.id ? (colors.accentSubtle || 'rgba(100,150,255,0.08)') : 'transparent',
                          '&:hover': { bgcolor: colors.surfaceHover },
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ color: colors.textPrimary, fontFamily: 'monospace', fontSize: 12 }}>
                            {match.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: colors.textMuted }}>
                            {scopeLabel} {match.domain && `• ${match.domain}`}{match.category && `/${match.category}`}
                            {match.version && ` • v${match.version}`}
                          </Typography>
                        </Box>
                        {match.isLocked && <LockIcon sx={{ fontSize: 14, color: colors.warning }} />}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </>
          )}

          {/* Create mode (or no matches found) */}
          {(importMode === 'create' || importMatches.length === 0) && (
            <Box>
              {importMatches.length === 0 && (
                <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1.5 }}>
                  No existing script found with the name "{importedScript?.meta?.name || importedScript?.fileName?.replace(/\.flex$/, '')}".
                  A new script will be created.
                </Typography>
              )}
              <TextField
                autoFocus
                fullWidth
                label="Command Name"
                value={importCreateForm.name}
                onChange={(e) => setImportCreateForm(prev => ({ ...prev, name: e.target.value }))}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
                  '& .MuiInputBase-input': { color: colors.textPrimary },
                  '& .MuiInputLabel-root': { color: colors.textSecondary },
                }}
              />
              <TextField
                fullWidth
                label="Display Name"
                value={importCreateForm.displayName}
                onChange={(e) => setImportCreateForm(prev => ({ ...prev, displayName: e.target.value }))}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
                  '& .MuiInputBase-input': { color: colors.textPrimary },
                  '& .MuiInputLabel-root': { color: colors.textSecondary },
                }}
              />
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Domain"
                  placeholder="e.g., wms"
                  value={importCreateForm.domain}
                  onChange={(e) => setImportCreateForm(prev => ({ ...prev, domain: e.target.value }))}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
                    '& .MuiInputBase-input': { color: colors.textPrimary },
                    '& .MuiInputLabel-root': { color: colors.textSecondary },
                  }}
                />
                <TextField
                  fullWidth
                  label="Category"
                  placeholder="e.g., receiving"
                  value={importCreateForm.category}
                  onChange={(e) => setImportCreateForm(prev => ({ ...prev, category: e.target.value }))}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
                    '& .MuiInputBase-input': { color: colors.textPrimary },
                    '& .MuiInputLabel-root': { color: colors.textSecondary },
                  }}
                />
              </Box>
              <TextField
                fullWidth
                label="Description (optional)"
                multiline
                rows={2}
                value={importCreateForm.description}
                onChange={(e) => setImportCreateForm(prev => ({ ...prev, description: e.target.value }))}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
                  '& .MuiInputBase-input': { color: colors.textPrimary },
                  '& .MuiInputLabel-root': { color: colors.textSecondary },
                }}
              />
            </Box>
          )}

          {/* Preview script body */}
          <Box sx={{ mt: 2, p: 1, bgcolor: colors.background, border: `1px solid ${colors.border}`, maxHeight: 120, overflow: 'auto' }}>
            <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mb: 0.5 }}>Script body preview:</Typography>
            <Typography
              variant="body2"
              component="pre"
              sx={{ color: colors.textSecondary, fontFamily: 'monospace', fontSize: 11, whiteSpace: 'pre-wrap', m: 0 }}
            >
              {importedScript?.scriptBody?.substring(0, 500)}{importedScript?.scriptBody?.length > 500 ? '...' : ''}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.border}` }}>
          <Button
            onClick={() => setImportDialogOpen(false)}
            sx={{ color: colors.textSecondary, borderRadius: 0 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleImportConfirm}
            disabled={importLoading || (importMode === 'create' && !importCreateForm.name.trim()) || (importMode === 'update' && !importSelectedId)}
            startIcon={importLoading ? <CircularProgress size={16} color="inherit" /> : <ImportIcon />}
            sx={{ bgcolor: colors.success, borderRadius: 0, '&:hover': { bgcolor: colors.successDark || colors.success } }}
          >
            {importLoading ? 'Importing...' : importMode === 'update' ? 'Update Command' : 'Create Command'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FlexWorkspace;
